import { Delete as DeleteIcon } from '@mui/icons-material';
import { Box, Button, IconButton, Paper, TextField, Typography } from '@mui/material';
import { HttpError, useModal } from '@refinedev/core';
import { Edit } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { Controller, useFieldArray } from 'react-hook-form';

import { AiRemirror } from '@attackiq/components/AiRemirror';

import { Selector } from '../../components/inputs';
import { Mitigation, MitigationReference } from '../../types';
import HistoryDrawer from '../scenarios/components/HistoryDrawer';

/**
 * Every field this form registers. `company` and `tracker_id` are create-only
 * (they are set once, by the hidden inputs on the create page), so they are not
 * part of the edit payload.
 */
type MitigationFormValues = {
  name: string;
  description: string;
  references: MitigationReference[];
  vendor_product_id: string | null;
};

const MitigationsEdit = () => {
  const methods = useForm<Mitigation, HttpError, MitigationFormValues>({
    refineCoreProps: {
      queryOptions: {
        gcTime: 0,
        staleTime: 0,
        select: data => {
          return {
            data: {
              ...data.data,
              references: data.data.references || [] // References is sometimes null but it should be an empty array when there are no references
            }
          };
        }
      }
    }
  });

  const {
    refineCore: { formLoading, query: queryResult },
    control,
    saveButtonProps,
    formState: { errors }
  } = methods;

  const {
    fields: referenceFields,
    append,
    remove
  } = useFieldArray({
    control,
    name: 'references',
    shouldUnregister: true
  });

  const drawer = useModal();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      <Paper variant="outlined">
        <Box
          sx={{
            p: 2
          }}
        >
          <Typography variant="subtitle2">Mitigation ID: {queryResult?.data?.data['id']}</Typography>
          <Typography variant="subtitle2">Tracker ID: {queryResult?.data?.data['tracker_id']}</Typography>
        </Box>
        <Box sx={{ mx: 1.5 }}>
          <Button
            type="button"
            variant="contained"
            onClick={() => {
              drawer.show();
            }}
          >
            HISTORY
          </Button>
        </Box>
      </Paper>
      <Edit isLoading={formLoading} saveButtonProps={saveButtonProps}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} component="form" autoComplete="off">
          <Controller
            rules={{ required: 'Mitigation name is required' }}
            name="name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                value={value || ''}
                onChange={onChange}
                error={!!errors.name}
                helperText={errors.name?.message}
                label="Mitigation Name"
                autoFocus
                fullWidth
                required
              />
            )}
          />
          <Typography variant="body1">Description*</Typography>
          <Controller
            rules={{ required: 'Mitigation description is required' }}
            name="description"
            control={control}
            render={({ field: { onChange, value } }) => {
              if (value === undefined) {
                return <>Loading...</>;
              }
              return <AiRemirror value={value || ''} onChange={onChange} />;
            }}
          />
          <Typography variant="h6" component="h1">
            References
          </Typography>
          {referenceFields.map((field, index) => (
            <Box
              sx={{
                display: 'flex',
                gap: 2
              }}
              key={field.id}
            >
              <Controller
                rules={{ required: 'Reference title is required' }}
                name={`references.${index}.title`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    error={!!errors.references?.[index]?.title}
                    helperText={errors.references?.[index]?.title?.message}
                    label="Reference title"
                    fullWidth
                    required
                  />
                )}
              />

              <Controller
                rules={{ required: 'Reference URL is required' }}
                control={control}
                name={`references.${index}.url`}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    label="Reference URL"
                    fullWidth
                    required
                    error={!!errors.references?.[index]?.url}
                    helperText={errors.references?.[index]?.url?.message}
                  />
                )}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <IconButton onClick={() => remove(index)} aria-label="delete">
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            onClick={() =>
              append({
                title: '',
                url: ''
              })
            }
          >
            Add new reference
          </Button>

          <Selector label="Vendor Product" resource="vendor_products" control={control} name="vendor_product_id" />
        </Box>
      </Edit>
      <HistoryDrawer drawer={drawer} selectedId={queryResult?.data?.data['id'] as string} model="Mitigation" />
    </Box>
  );
};

export default MitigationsEdit;
